import React, { ReactElement, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useProjectOrganizationId } from 'store/project';
import { useProcessesObject } from 'store/process';
import { filterOutSuperAdmin } from 'shared/domain/user/filterSuperAdmin';
import { userModelToUserOnView } from 'shared/domain/user/mapping/toView';
import { useUsers } from '../../dataProviders/withUsers';
import { useSites } from 'store/site';
import { UserDataGrid } from '../UserDataGrid';
import { UserOnView } from 'shared/domain/user/types/view';

export function UserTable(): ReactElement {
  const {
    filtered: { items: users },
    emptyListReason,
    loading,
  } = useUsers();
  const organizationId = useProjectOrganizationId();
  const processes = useProcessesObject();
  const {
    items: sites,
    loading: loadingSites,
  } = useSites();
  const intl = useIntl();

  const usersWithoutSuperAdmins = useMemo(
    () => users.filter(filterOutSuperAdmin),
    [users]
  );

  const usersOnView = useMemo(
    () =>
      usersWithoutSuperAdmins
        .map((user) =>
          userModelToUserOnView({
            userModel: user,
            sites,
            processes: Object.values(processes),
            intl,
            organizationId,
          })
        )
        .filter((user): user is UserOnView => user !== null),
    [usersWithoutSuperAdmins, sites, processes, intl, organizationId]
  );

  // Show loading if users or sites are still loading
  const isLoading = loading || loadingSites;

  return (
    <UserDataGrid
      data={usersOnView}
      loading={isLoading}
      emptyListReason={isLoading ? undefined : emptyListReason}
    />
  );
}
